import React, { useState } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  Button,
  IconButton,
  Avatar,
  Box,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { studentService } from 'src/services/student.service';
import { studentBehaviourService } from 'src/services/student-behaviour.service';
import { StudentBehaviour } from 'src/types/behaviour.types';
import CustomPagination from 'src/components/CustomPagination';
import CreatedBy from 'src/components/CreatedBy';
import { useAuth } from 'src/context/auth/AuthContext';
import { useViewScope } from 'src/hooks/useViewScope';
import { useDebounce } from 'src/hooks/useDebounce';
import moment from 'moment';

export const StudentBehavious: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getBaseRoute, hasPermission, can, user } = useAuth();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'highlight' | 'challenge'>('ALL');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBehavior, setSelectedBehavior] = useState<StudentBehaviour | null>(null);

  const debouncedSearch = useDebounce(searchInput, 300);

  const { isSelfScoped: isStudent, ownerFilterId: teacherUserId } = useViewScope('street_cred');
  const canCreateBehavior = hasPermission('street_cred.manage');

  // Resolve student ID if authenticated user is a student
  const { data: studentId } = useQuery({
    queryKey: ['studentIdByUserId', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: isStudent && !!user?.id,
  });

  const {
    data: behavioursResponse,
    isLoading,
  } = useQuery({
    queryKey: ['studentBehaviours', page, rowsPerPage, debouncedSearch, typeFilter, teacherUserId, studentId, isStudent],
    queryFn: () =>
      studentBehaviourService.getStudentBehaviours({
        teacherUserId: isStudent ? undefined : teacherUserId,
        studentId: isStudent ? (studentId as string) : undefined,
        search: debouncedSearch,
        limit: rowsPerPage,
        page,
      }),
    enabled: !isStudent || !!studentId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => studentBehaviourService.deleteStudentBehaviour(id),
    onSuccess: () => {
      toast.success('Behavior entry deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['studentBehaviours'] });
      queryClient.invalidateQueries({ queryKey: ['streetCredMetrics'] });
      setDeleteModalOpen(false);
      setSelectedBehavior(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to delete behavior entry');
    },
  });

  const rawList = behavioursResponse?.data || [];
  const filteredList = rawList.filter((item) => {
    if (typeFilter === 'ALL') return true;
    return item?.behaviour_types?.type === typeFilter;
  });

  const totalCount = behavioursResponse?.total || 0;

  const handleOpenDelete = (behavior: StudentBehaviour) => {
    setSelectedBehavior(behavior);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedBehavior?.id) {
      deleteMutation.mutate(selectedBehavior.id);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* High-Density Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        {/* Search & Category Filter Chips */}
        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" sx={{ flexGrow: 1 }}>
          <TextField
            size="small"
            placeholder={isStudent ? 'Search by behavior title or notes...' : 'Search scholar name, email, behavior...'}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="solar:magnifer-linear" width={18} color="#64748B" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <Icon icon="solar:close-circle-bold" width={15} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                height: 38,
                fontSize: '0.875rem',
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                width: { xs: '100%', sm: 300, md: 340 },
              },
            }}
          />

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              label="All Records"
              size="small"
              clickable
              onClick={() => {
                setTypeFilter('ALL');
                setPage(1);
              }}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: 1.5,
                bgcolor: typeFilter === 'ALL' ? 'primary.main' : 'background.paper',
                color: typeFilter === 'ALL' ? 'primary.contrastText' : 'text.secondary',
                border: '1px solid',
                borderColor: typeFilter === 'ALL' ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: typeFilter === 'ALL' ? 'primary.dark' : 'action.hover' },
              }}
            />
            <Chip
              icon={<Icon icon="solar:medal-ribbon-star-bold-duotone" width={16} color={typeFilter === 'highlight' ? '#FFFFFF' : '#059669'} />}
              label="Highlights"
              size="small"
              clickable
              onClick={() => {
                setTypeFilter('highlight');
                setPage(1);
              }}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: 1.5,
                bgcolor: typeFilter === 'highlight' ? '#059669' : 'background.paper',
                color: typeFilter === 'highlight' ? '#FFFFFF' : '#059669',
                border: '1px solid',
                borderColor: typeFilter === 'highlight' ? '#059669' : 'divider',
                '&:hover': { bgcolor: typeFilter === 'highlight' ? '#047857' : 'rgba(5, 150, 105, 0.08)' },
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            <Chip
              icon={<Icon icon="solar:shield-warning-bold-duotone" width={16} color={typeFilter === 'challenge' ? '#FFFFFF' : '#DC2626'} />}
              label="Challenges"
              size="small"
              clickable
              onClick={() => {
                setTypeFilter('challenge');
                setPage(1);
              }}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: 1.5,
                bgcolor: typeFilter === 'challenge' ? '#DC2626' : 'background.paper',
                color: typeFilter === 'challenge' ? '#FFFFFF' : '#DC2626',
                border: '1px solid',
                borderColor: typeFilter === 'challenge' ? '#DC2626' : 'divider',
                '&:hover': { bgcolor: typeFilter === 'challenge' ? '#B91C1C' : 'rgba(220, 38, 38, 0.08)' },
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Stack>
        </Stack>

        {/* Action Button */}
        {canCreateBehavior && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              size="small"
              startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
              onClick={() => navigate(getBaseRoute('/add-student-behaviour'))}
              sx={{
                height: 38,
                borderRadius: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2.25,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
              }}
            >
              + Log Behavior
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Scholar Behavior Table with spacious row height */}
      <TableContainer>
        <Table>
          <thead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {!isStudent && (
                <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                  Scholar
                </TableCell>
              )}
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Behavior & Type
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Points Impact
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Notes & Context
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Logged At
              </TableCell>
              {!isStudent && (
                <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                  Logged By
                </TableCell>
              )}
              {!isStudent && (
                <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </thead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={isStudent ? 4 : 7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} thickness={4} />
                  <Typography variant="caption" sx={{ mt: 1.25, color: 'text.secondary', fontWeight: 500, display: 'block', fontSize: '0.8125rem' }}>
                    Loading behavior logs...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredList.length > 0 ? (
              filteredList.map((row: StudentBehaviour) => {
                const studentName = `${row?.student?.user?.firstname || ''} ${row?.student?.user?.lastname || ''}`.trim() || 'Unnamed Scholar';
                const scholarId = row?.studentId || (row?.student as any)?.id;
                const viewStudentPath = scholarId ? getBaseRoute(`/view-student/${scholarId}`) : null;
                const isHighlight = row?.behaviour_types?.type === 'highlight';
                const points =
                  row?.points !== 0 && row?.points !== null && row?.points !== undefined
                    ? Number(row.points)
                    : row?.behaviour_types?.points !== 0 &&
                      row?.behaviour_types?.points !== null &&
                      row?.behaviour_types?.points !== undefined
                    ? Number(row.behaviour_types.points)
                    : isHighlight
                    ? 10
                    : -10;

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      transition: 'background-color 0.15s ease',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    {/* Scholar Cell (Clickable Profile Link - Educators Only) */}
                    {!isStudent && (
                      <TableCell sx={{ py: 2 }}>
                        <Tooltip title={viewStudentPath ? 'View Scholar Profile' : ''}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            onClick={(e) => {
                              if (viewStudentPath) {
                                e.stopPropagation();
                                navigate(viewStudentPath);
                              }
                            }}
                            sx={{
                              cursor: viewStudentPath ? 'pointer' : 'default',
                              width: 'fit-content',
                              '&:hover .scholar-name': {
                                color: viewStudentPath ? 'primary.main' : 'text.primary',
                                textDecoration: viewStudentPath ? 'underline' : 'none',
                              },
                              '&:hover .scholar-avatar': {
                                transform: viewStudentPath ? 'scale(1.06)' : 'none',
                                boxShadow: viewStudentPath ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                              },
                            }}
                          >
                            <Avatar
                              className="scholar-avatar"
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                color: isHighlight ? '#059669' : '#DC2626',
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {studentName.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography
                                className="scholar-name"
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  fontSize: '0.875rem',
                                  lineHeight: 1.25,
                                  transition: 'color 0.15s ease',
                                }}
                              >
                                {studentName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 400 }}>
                                {row?.student?.user?.email || 'No email'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                    )}

                    {/* Behavior Title & Type with Duotone Icon */}
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                            color: isHighlight ? '#059669' : '#DC2626',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon
                            icon={isHighlight ? 'solar:medal-ribbon-star-bold-duotone' : 'solar:danger-triangle-bold-duotone'}
                            width={19}
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.25 }}>
                            {row?.behaviour_types?.title || 'General Behavior'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', textTransform: 'capitalize', fontWeight: 400 }}>
                            {row?.behaviour_types?.type || 'Record'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Points Impact Badge */}
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={
                          <Icon
                            icon={isHighlight || points >= 0 ? 'solar:medal-star-bold-duotone' : 'solar:danger-triangle-bold-duotone'}
                            width={13}
                            color={isHighlight || points >= 0 ? '#059669' : '#DC2626'}
                          />
                        }
                        label={`${points >= 0 ? '+' : ''}${points} pts`}
                        size="small"
                        sx={{
                          height: 25,
                          fontSize: '0.8125rem',
                          fontWeight: 700,
                          borderRadius: 1.25,
                          bgcolor: isHighlight || points >= 0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                          color: isHighlight || points >= 0 ? '#059669' : '#DC2626',
                          border: '1px solid',
                          borderColor: isHighlight || points >= 0 ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                        }}
                      />
                    </TableCell>

                    {/* Notes & Context */}
                    <TableCell sx={{ py: 2, maxWidth: 320 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {row?.notes || '--'}
                      </Typography>
                    </TableCell>

                    {/* Date */}
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.8125rem', display: 'block' }}>
                        {moment(row?.createdAt).format('MMM DD, YYYY')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {moment(row?.createdAt).format('h:mm A')}
                      </Typography>
                    </TableCell>

                    {/* Logged By (Educators/Admins Only) */}
                    {!isStudent && (
                      <TableCell sx={{ py: 2 }}>
                        <CreatedBy creator={row?.creator} />
                      </TableCell>
                    )}

                    {/* Actions (Educators/Admins Only) */}
                    {!isStudent && (
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                          <Tooltip title="Edit Behavior">
                            <IconButton
                              size="small"
                              onClick={() => navigate(getBaseRoute(`/edit-student-behavior/${row.id}`))}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}
                            >
                              <Icon icon="solar:pen-2-bold" width={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Entry">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(row)}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'rgba(239, 68, 68, 0.08)' } }}
                            >
                              <Icon icon="solar:trash-bin-trash-bold" width={18} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={isStudent ? 4 : 7} align="center" sx={{ py: 6 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                      color: 'text.disabled',
                    }}
                  >
                    <Icon icon="solar:star-rings-bold-duotone" width={26} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                    No behavior records found
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 0.5, display: 'block' }}>
                    {searchInput || typeFilter !== 'ALL' ? 'Try adjusting your search query or filters' : 'Start logging scholar behavior points and recognitions'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
                    onClick={() => navigate(getBaseRoute('/add-student-behaviour'))}
                    sx={{ mt: 2, borderRadius: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem' }}
                  >
                    Log First Behavior
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalCount > 0 && (
        <CustomPagination
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onPageChange={(_, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(1);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: 'rgba(239, 68, 68, 0.12)',
              color: '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon icon="solar:trash-bin-trash-bold" width={18} />
          </Box>
          Delete Behavior Entry
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            Are you sure you want to remove this behavior record? This will soft-delete the log entry from the scholar's history.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDeleteModalOpen(false)}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={deleteMutation.isPending}
            onClick={handleConfirmDelete}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700 }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default StudentBehavious;
